import { apiClient } from './api-client';

// ==================== Enums ====================

export enum SurveyTrigger {
  // 'resolved' and 'closed' are independent status-driven triggers; a tenant
  // can survey on either or both terminal transitions.
  TICKET_RESOLVED = 'ticket_resolved',
  TICKET_CLOSED = 'ticket_closed',
  CONVERSATION_ENDED = 'conversation_ended',
  MANUAL = 'manual',
}

export enum SurveyScale {
  STARS_5 = 'stars_5',
  STARS_10 = 'stars_10',
  NPS = 'nps',
  THUMBS = 'thumbs',
  EMOJI = 'emoji',
}

export enum SurveyChannel {
  EMAIL = 'email',
  CHAT = 'chat',
  WIDGET = 'widget',
  SMS = 'sms',
}

// ==================== Interfaces ====================

export interface SurveyQuestion {
  id: string;
  text: string;
  scale: SurveyScale;
  required: boolean;
  lowLabel?: string;
  highLabel?: string;
}

export interface SurveyFollowUp {
  enabled: boolean;
  questionText?: string;
  showBelowRating?: number;
}

export interface SurveyBranding {
  logoUrl?: string;
  primaryColor?: string;
  backgroundColor?: string;
  thankYouMessage?: string;
}

export interface CsatConfig {
  id: string;
  tenantId: string;
  enabled: boolean;
  triggers: SurveyTrigger[];
  channels: SurveyChannel[];
  questions: SurveyQuestion[];
  followUp: SurveyFollowUp;
  branding: SurveyBranding;
  delayHours: number;
  samplePercentage: number;
  excludeRepeats: boolean;
  repeatCooldownDays: number;
  emailSubject?: string;
  emailTemplate?: string;
}

export interface UpdateCsatConfigDto {
  enabled?: boolean;
  triggers?: SurveyTrigger[];
  channels?: SurveyChannel[];
  questions?: SurveyQuestion[];
  followUp?: SurveyFollowUp;
  branding?: SurveyBranding;
  delayHours?: number;
  samplePercentage?: number;
  excludeRepeats?: boolean;
  repeatCooldownDays?: number;
  emailSubject?: string;
  emailTemplate?: string;
}

export interface CsatResponse {
  id: string;
  tenantId: string;
  ticketId: string;
  customerId: string;
  rating: number;
  comment?: string;
  questionId: string;
  channel: SurveyChannel;
  createdAt: string;
}

export interface ChannelStats {
  count: number;
  avgRating: number;
  ratingDistribution: Record<number, number>;
}

export interface CsatStats {
  averageScore: number;
  totalResponses: number;
  responseRate: number;
  distribution: Record<number, number>;
  trend: Array<{ date: string; average: number; count: number }>;
  channelBreakdown: Record<string, ChannelStats>;
}

// ==================== Service ====================

class CsatService {
  async getConfig(): Promise<CsatConfig> {
    const response = await apiClient.get<CsatConfig>('/csat/config');
    return response.data;
  }

  async updateConfig(dto: UpdateCsatConfigDto): Promise<CsatConfig> {
    // Strip server-owned fields. Callers often pass the full CsatConfig object
    // (structural typing lets them), but the backend DTO rejects id/tenantId.
    const { id: _id, tenantId: _tenantId, ...payload } = dto as CsatConfig;
    const response = await apiClient.put<CsatConfig>('/csat/config', payload);
    return response.data;
  }

  async getResponses(params?: {
    startDate?: string;
    endDate?: string;
    rating?: number;
    channel?: SurveyChannel;
    customerId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: CsatResponse[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.rating) queryParams.append('rating', String(params.rating));
    if (params?.channel) queryParams.append('channel', params.channel);
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const response = await apiClient.get<{ data: CsatResponse[]; total: number }>(
      `/csat/responses?${queryParams.toString()}`
    );
    return response.data;
  }

  async getStats(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<CsatStats> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await apiClient.get<CsatStats>(`/csat/stats?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Send a test survey to `email`.
   *
   * `config` carries the settings page's UNSAVED working copy. Without it the
   * backend rendered the test email from the last saved row, so editing the
   * subject or template and pressing Send Test — the button sits right under the
   * template box — produced an email built from the previous version, silently.
   */
  async sendTestSurvey(
    email: string,
    config?: UpdateCsatConfigDto,
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/csat/test-survey',
      { email, ...(config && { config }) }
    );
    return response.data;
  }

  /**
   * Manually trigger a CSAT survey for a specific ticket. Used by the
   * "Send Survey" button on the ticket detail page. Bypasses cooldown
   * and sample-percentage rules — an agent's deliberate click overrides
   * automation. Still respects the duplicate-per-ticket guard.
   */
  async sendManualSurvey(payload: {
    ticketId: string;
    customerId?: string;
  }): Promise<{ success: boolean; surveyId?: string; message: string }> {
    const response = await apiClient.post<{
      success: boolean;
      surveyId?: string;
      message: string;
    }>('/csat/manual-survey', payload);
    return response.data;
  }

  // ==================== Helpers ====================

  getTriggerLabel(trigger: SurveyTrigger): string {
    const labels: Record<SurveyTrigger, string> = {
      [SurveyTrigger.TICKET_RESOLVED]: 'Ticket Resolved',
      [SurveyTrigger.TICKET_CLOSED]: 'Ticket Closed',
      // Fires when ANY chat session ends, not just AI-only ones: the
      // channel-gateway publishes chat.session_ended unconditionally
      // (chat.service.ts publishChatSessionEndedEvent) and the ticket-service
      // consumer offers every one of them to the orchestrator. It was labelled
      // "(bot-only)" until 2026-08-04, which was wrong.
      //
      // Consequence worth knowing: an agent closing a chat sets the ticket to
      // resolved, so with both this and "Ticket Resolved" ticked the same
      // conversation can produce two surveys.
      [SurveyTrigger.CONVERSATION_ENDED]: 'Conversation Ended',
      // Not an automation rule like the others — it's the permission that lets
      // agents fire a survey from the ticket page's "Send Survey" button.
      // Enforced by CsatOrchestratorService.sendManualSurvey.
      [SurveyTrigger.MANUAL]: 'Manual',
    };
    return labels[trigger] || trigger;
  }

  getScaleLabel(scale: SurveyScale): string {
    const labels: Record<SurveyScale, string> = {
      [SurveyScale.STARS_5]: '5-Star Rating',
      [SurveyScale.STARS_10]: '10-Star Rating',
      [SurveyScale.NPS]: 'Net Promoter Score (0-10)',
      [SurveyScale.THUMBS]: 'Thumbs Up/Down',
      [SurveyScale.EMOJI]: 'Emoji Scale',
    };
    return labels[scale] || scale;
  }

  getChannelLabel(channel: SurveyChannel): string {
    const labels: Record<SurveyChannel, string> = {
      [SurveyChannel.EMAIL]: 'Email',
      [SurveyChannel.CHAT]: 'Live Chat',
      [SurveyChannel.WIDGET]: 'Website Widget',
      [SurveyChannel.SMS]: 'SMS',
    };
    return labels[channel] || channel;
  }

  getScaleOptions(): Array<{ value: SurveyScale; label: string }> {
    return Object.values(SurveyScale).map((scale) => ({
      value: scale,
      label: this.getScaleLabel(scale),
    }));
  }

  /**
   * Triggers offered in the CSAT settings picker.
   *
   * All four are offered, but MANUAL is a different kind of switch: the other
   * three say "send a survey when X happens", while MANUAL says "agents are
   * allowed to send one by hand". It was pulled from this list on 2026-08-03
   * because nothing read it — `sendManualSurvey` ignored the trigger list, so
   * ticking or unticking it changed nothing. It is back because that gate now
   * exists in CsatOrchestratorService.sendManualSurvey (2026-08-04), which
   * makes it a real permission. Existing tenants were granted it by
   * centraOnePlus-shared/prisma/scripts/backfill-csat-manual-trigger.sql.
   */
  getTriggerOptions(): Array<{ value: SurveyTrigger; label: string }> {
    return Object.values(SurveyTrigger).map((trigger) => ({
      value: trigger,
      label: this.getTriggerLabel(trigger),
    }));
  }

  getChannelOptions(): Array<{ value: SurveyChannel; label: string }> {
    return Object.values(SurveyChannel).map((channel) => ({
      value: channel,
      label: this.getChannelLabel(channel),
    }));
  }

  formatRating(rating: number, scale: SurveyScale): string {
    switch (scale) {
      case SurveyScale.STARS_5:
        return `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
      case SurveyScale.STARS_10:
        return `${rating}/10`;
      case SurveyScale.NPS:
        return `${rating}/10`;
      case SurveyScale.THUMBS:
        return rating >= 3 ? '👍' : '👎';
      case SurveyScale.EMOJI:
        const emojis = ['😞', '😕', '😐', '🙂', '😊'];
        return emojis[Math.min(rating - 1, 4)];
      default:
        return String(rating);
    }
  }

  getSatisfactionLevel(score: number): { label: string; color: string } {
    if (score >= 4.5) return { label: 'Excellent', color: 'text-green-600' };
    if (score >= 4.0) return { label: 'Good', color: 'text-green-500' };
    if (score >= 3.0) return { label: 'Average', color: 'text-yellow-600' };
    if (score >= 2.0) return { label: 'Poor', color: 'text-orange-600' };
    return { label: 'Very Poor', color: 'text-red-600' };
  }
}

export const csatService = new CsatService();
export default csatService;
